import * as React from 'react';
import { Invoice } from 'fs-api-client';
import { formatAmount, getDateFormatWithoutDay } from '../../../webportal/utils/utilFunctions';
import { Nullable } from '../../../webportal/utils/types';
import PaymentStatusEnum = Invoice.PaymentStatusEnum;
import { ErrorRenderer } from '../common/ErrorRenderer';
import { FETCHING_MESSAGE } from '../../utils/constants';
import { DownloadInCloudIcon, renderWaitingForSuccessElement } from '../../../webportal/components/common/Icons';
import { createDefaultApi } from '../../../webportal/utils/createApi';
import { InvoiceBreakdownModal } from '../billing/InvoiceBreakdownModal';

interface UserInvoicesProps {
    userId: string;
}

interface UserInvoicesState {
    invoices: Invoice[];
    selectedInvoice: Nullable<Invoice>;
    fetching: boolean;
    hasError: boolean;
    errorMessage: string;
}

export class UserInvoices extends React.Component<UserInvoicesProps, UserInvoicesState> {
    constructor(props: UserInvoicesProps) {
        super(props);

        this.fetchInvoices = this.fetchInvoices.bind(this);
        this.onInvoiceClicked = this.onInvoiceClicked.bind(this);
        this.onInvoiceModalClose = this.onInvoiceModalClose.bind(this);

        this.state = {
            invoices: [],
            selectedInvoice: null,
            fetching: false,
            hasError: false,
            errorMessage: '',
        };

    }

    async componentDidMount() {
        await this.fetchInvoices();
    }

    render() {
        return (
            <React.Fragment>
                <InvoiceBreakdownModal
                    invoice={this.state.selectedInvoice}
                    onInvoiceModalClose={this.onInvoiceModalClose}
                />
                <div>
                    <h2 className="listTitle">Past Invoices</h2>
                    {this.state.invoices.map((invoice) => {
                        let invoicePaymentStatus = this.resolveInvoicePaymentStatus(invoice.paymentStatus);
                        const className = this.getInvoiceClassName(invoice.paymentStatus);
                        return (
                            <React.Fragment key={invoice.id}>
                                <div className={`row listItem vcenter invoice ${className}`}>
                                    <div className="status">{invoicePaymentStatus}</div>
                                    <div className="fullWidth">{getDateFormatWithoutDay(invoice.date)}</div>
                                    <div>
                                        {formatAmount(invoice.amountDue, invoice.currency)}
                                    </div>
                                    {invoice.filespaceBreakdown && invoice.filespaceBreakdown.length > 0 && (
                                        <button
                                            data-toggle="modal"
                                            className="detailLink"
                                            data-target="#filespaceBreakdownModal"
                                            onClick={() => this.onInvoiceClicked(invoice)}
                                        >Details
                                        </button>
                                    )}
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            window.location.href = `${invoice.pdfUrl}`;
                                        }}
                                        className="light"
                                    >
                                        <DownloadInCloudIcon width="20px"/> PDF
                                    </button>
                                </div>
                            </React.Fragment>
                        );
                    })}
                    {this.state.errorMessage && this.state.errorMessage}
                    {this.state.fetching && renderWaitingForSuccessElement(FETCHING_MESSAGE)}
                    {this.state.hasError && <ErrorRenderer errorMessage={this.state.errorMessage}/>}
                </div>
            </React.Fragment>
        );
    }

    private resolveInvoicePaymentStatus(paymentStatus: PaymentStatusEnum): string {
        switch (paymentStatus) {
            case PaymentStatusEnum.NotPaid:
                return 'Not Paid';
            case PaymentStatusEnum.Paid:
                return 'Paid';
            case PaymentStatusEnum.PastDue:
                return 'Past Due';
            case PaymentStatusEnum.Voided:
                return 'Voided';
            default:
                return 'Unknown Status';
        }
    }

    private getInvoiceClassName(paymentStatus: PaymentStatusEnum): string {
        switch (paymentStatus) {
            case PaymentStatusEnum.NotPaid:
                return 'not-paid';
            case PaymentStatusEnum.Paid:
                return 'paid';
            case PaymentStatusEnum.PastDue:
                return 'past-due';
            case PaymentStatusEnum.Voided:
                return 'voided';
            default:
                return '';
        }
    }

    private onInvoiceClicked(invoice: Invoice) {
        this.setState({ selectedInvoice: invoice });
    }

    private onInvoiceModalClose() {
        this.setState({ selectedInvoice: null });
    }

    private async fetchInvoices(): Promise<void> {
        this.setState({ fetching: true });
        const api = await createDefaultApi();
        try {
            const invoices = (await api.getUserInvoices(this.props.userId)).body;
            if (invoices.length === 0) {
                this.setState(
                    {
                        errorMessage: 'User has no invoices.'
                    }
                );
            }
            this.setState({invoices: invoices, fetching: false});
        } catch (e) {
            this.setState(
                {
                    hasError: true,
                    errorMessage: e.message,
                    fetching: false
                }
            );
        }
    }
}



// WEBPACK FOOTER //
// ./src/backoffice/components/user/UserInvoices.tsx